import { TextMetrics as PixiTextMetrics } from 'pixi.js';
import { TextLineMetrics } from './TextLineMetrics';
import { TextWordMetrics } from './TextWordMetrics';
import { getGlobalFontManager } from './FontManager';
import { WordToken } from './WordToken';
import { WordElementToken } from './WordElementToken';
import type WordElementDataStore from './WordElementDataStore';
import type { WordStyle } from './WordStyle';


export function isNewline(text: string): boolean {
    // isNewline is declared as private inside PIXI`
    return (PixiTextMetrics as any).isNewline(text);
}

export function isBreakingSpace(text: string): boolean {
    // isBreakingSpace is declared as private inside PIXI
    return (PixiTextMetrics as any).isBreakingSpace(text);
}

function tokenize(text: string): string[] {
    // tokenize is declared as private inside PIXI
    return (PixiTextMetrics as any).tokenize(text);
}

/**
 * Check to see if any text words are too long for a single line and split as necessary.
 * @param initialTextWordMetrics - list of text tokens
 * @param wordWrap - if words should be wrapped
 */
function fitTextWordsToLine(
    initialTextWordMetrics: TextWordMetrics[], wordWrap: boolean, wordWrapWidth: number, wordElementDataStore: WordElementDataStore
): TextWordMetrics[] {
    // Ensure that each text words fits within the max line width if specified
    if (!wordWrap) {
        return initialTextWordMetrics;
    }

    const wrappedTextWordMetrics = initialTextWordMetrics.reduce((result, textWordMetrics) => {
        // This helper slices the current TextWordMetrics and makes sure there is a cache entry
        // in the WordElementDataStore for the new token.
        const slice = (start, end) => {
            // Cut the word
            const slicedWordToken = textWordMetrics.slice(start, end);
            // Add to the WordElementDataStore to cache metrics and WordElements
            wordElementDataStore.addItems(slicedWordToken.wordElementTokens);
            // Build the new textword
            const slicedTextWordMetrics = createTextWordMetrics([slicedWordToken], wordElementDataStore);
            // Add to word list
            result.push(slicedTextWordMetrics[0]);
        };

        if (textWordMetrics.advanceWidth >= wordWrapWidth) {
            // Cancel all WordElements of the word before splitting; we won't need these
            // because only the sliced version will be used.
            textWordMetrics.wordElements.forEach(wordElement => {
                wordElementDataStore.cancelWordElementSprite(wordElement.wordElementToken);
            });

            let currentStart = 0;
            const numChars = textWordMetrics.numChars;
            for (let i = 1; i < numChars; i++) {
                if (textWordMetrics.partialAdvanceWidth(currentStart, i + 1) > wordWrapWidth) {
                    slice(currentStart, i);
                    currentStart = i;
                }
            }
            if (currentStart < numChars) {
                slice(currentStart, numChars);
            }
        }
        else {
            result.push(textWordMetrics);
        }
        return result;
    }, []);

    return wrappedTextWordMetrics;
}

/**
 * Assign text words to lines, if word wrap is enabled then fit text words based on
 * the word wrap width
 */
export function assignTextWordsToLines(
    textWordMetricsList: TextWordMetrics[], wordWrap: boolean, wordWrapWidth: number
): TextLineMetrics[] {
    // assign each token to a line
    const lines = [];
    let currentLine = new TextLineMetrics();
    textWordMetricsList.forEach(textWordMetrics => {
        // if word is a new line
        if (isNewline(textWordMetrics.text)) {
            // first add new line word to current line and then store the new line
            currentLine.insertTextWordMetrics(textWordMetrics);
            lines.push(currentLine);
            currentLine = new TextLineMetrics();
            return;
        }

        // if the word doesn't fit on the current line
        if (wordWrap && textWordMetrics.advanceWidth + currentLine.advanceWidth > wordWrapWidth) {
            lines.push(currentLine);
            currentLine = new TextLineMetrics();
        }

        // add word to current line
        currentLine.insertTextWordMetrics(textWordMetrics);
    });

    // finish the remaining line
    if (currentLine.numTextWords > 0) {
        lines.push(currentLine);
    }

    return lines;
}

/**
 * Converts a text to WordTokens of the appropriate style.
 *
 * @param highlightAreas - Array of IntArrays[2] defining the highlight areas
 */
export function getTokenList(text: string, normalStyle: WordStyle, highlightAreas: number[][], highlightStyle: WordStyle = normalStyle): WordToken[] {
    // break text into words, spaces, and newline chars
    const tokens = tokenize(text);
    return createWordTokens(tokens, highlightAreas, normalStyle, highlightStyle);
}

/**
 * Layout each words on separate lines.
 */
export function layoutTextWords(textWordMetrics: TextWordMetrics[], wordElementDataStore: WordElementDataStore, wordWrap = false, wordWrapWidth = Infinity): TextLineMetrics[] {
    if (textWordMetrics.length === 0) {
        return [];
    }
    // ensure each text word fits within a line
    const fittedTWMetrics = fitTextWordsToLine(textWordMetrics, wordWrap, wordWrapWidth, wordElementDataStore);

    // create text lines
    const lines = assignTextWordsToLines(fittedTWMetrics, wordWrap, wordWrapWidth);

    // replace leading and trailing spaces with zero-bound word element
    lines.forEach(line => line.removeLeadingTrailingSpaces());

    // after removing spaces it could be that there's no text left on the line,
    // so in that case remove the line
    return lines.filter(line => line.text);
}

/**
 * Create the initial TextWord Metrics.
 * @param wordTokens - list of text tokens
 */
export function createTextWordMetrics(wordTokens: WordToken[], wordElementDataStore: WordElementDataStore): TextWordMetrics[] {
    const textWordMetricsList = [];
    wordTokens.forEach((wordToken) => {
        const textWordMetrics = new TextWordMetrics();
        wordToken.wordElementTokens.forEach((wordElementToken) => {
            const wordElementMetrics = wordElementDataStore.getNextWordElementMetrics(wordElementToken);
            textWordMetrics.addElement(wordElementMetrics);
        });
        if (textWordMetrics.numWordElements > 0) {
            textWordMetricsList.push(textWordMetrics);
        }
    });
    return textWordMetricsList;
}

/**
 * Given a list of tokens (strings) and a list of delimited areas to highlight, creates a list of
 * WordTokens highlighted appropriately. WordTokens store text in WordElementTokens, which can be split
 * by this function to create the required highlight.  This function also finds
 * which font will render each WordElement, splitting tokens when needed.
 *
 * @param wordTokensStrings - list of word tokens
 * @param highlightAreas - Array of IntArrays[2] defining the highlight areas
 * @return list of annotated text tokens.
 */
export function createWordTokens(
    wordTokenStrings: string[], highlightAreas: number[][], normalStyle: WordStyle, highlightStyle: WordStyle
): WordToken[] {
    const annotatedWordTokens: WordToken[] = [];

    // Build an initial array of WordTokens
    wordTokenStrings.forEach(token => {
        annotatedWordTokens.push(new WordToken(token, false));
    });

    // Highlight tokens
    highlightAreas.forEach(highlight => {
        // process each highlight pair
        const highlightStart = highlight[0];
        const highlightEnd = highlight[1];
        let charCounter = 0;

        // process every token to apply the highlight
        for (const token of annotatedWordTokens) {
            const tokenStart = charCounter;
            const tokenEnd = tokenStart + token.length;
            if (tokenStart > highlightEnd) {
                // passed the end of the highlight so can skip the rest of the tokens
                break;
            }
            if (highlightStart < tokenEnd && highlightEnd > tokenStart) {
                token.highlight(highlightStart - tokenStart, highlightEnd - tokenStart);
            }
            charCounter += token.length;
        }
    });

    // Find fonts for each token. It is possible that not all characters can be rendered with the same font.
    const fontManager = getGlobalFontManager();
    const wordTokensWithFonts: WordToken[] = [];

    annotatedWordTokens.forEach((wordToken) => {
        const newWordToken = new WordToken();
        wordToken.wordElementTokens.forEach(wordElementToken => {
            const style = wordElementToken.highlighted ? highlightStyle : normalStyle;

            const fontList = fontManager.getFontListForString(wordElementToken.text, style.fontUrl);

            fontList.forEach(fontEntry => {
                newWordToken.add(new WordElementToken(fontEntry.text, wordElementToken.highlighted, fontEntry.font, style));
            });
        });
        wordTokensWithFonts.push(newWordToken);
    });

    return wordTokensWithFonts;
}
